// Left column — current ruleset rendered as the .agi-ish DSL.
// Cards live-update when the loop deploys a new rule (the freshest
// rule briefly highlights so you can see what just landed).

import { useEffect, useRef, useState } from "react";
import { useLoopState } from "../lib/use-loop-state";
import { formatPredicate, type Rule } from "../lib/dsl";

export function RulesPanel() {
  const state = useLoopState();
  return (
    <section className="rp">
      <header className="rp-head">
        <span className="rp-title">CURRENT RULES</span>
        <span className="rp-count">{state.rules.length}</span>
      </header>
      <div className="rp-list">
        {state.rules.map((rule, i) => (
          <RuleCard key={rule.id} rule={rule} isNewest={i === state.rules.length - 1} />
        ))}
      </div>
      <Styles />
    </section>
  );
}

function RuleCard({ rule, isNewest }: { rule: Rule; isNewest: boolean }) {
  // Brief highlight in two cases: when this card first becomes the newest
  // (a fresh rule_add landed), AND when its predicate text changes while
  // staying in place (a LENGTH ratchet rewrote it). The second case is
  // what makes a ratchet visible — the length card pops without moving.
  const [highlight, setHighlight] = useState(false);
  const firstSeenAsNewest = useRef<string | null>(null);
  const lastPred = useRef<string | null>(null);
  const pred = formatPredicate(rule.predicate);
  useEffect(() => {
    const becameNewest = isNewest && firstSeenAsNewest.current !== rule.id;
    const predChanged = lastPred.current !== null && lastPred.current !== pred;
    lastPred.current = pred;
    if (becameNewest || predChanged) {
      if (becameNewest) firstSeenAsNewest.current = rule.id;
      setHighlight(true);
      const t = setTimeout(() => setHighlight(false), 1800);
      return () => clearTimeout(t);
    }
  }, [isNewest, rule.id, pred]);

  return (
    <div className={`rp-card ${highlight ? "rp-card-new" : ""}`}>
      <div className="rp-card-head">
        <span className="rp-card-kw">RULE</span>
        <span className="rp-card-name">{rule.name}</span>
      </div>
      <div className="rp-card-body">
        <span className="rp-card-brace">{"{"}</span>
        <span className="rp-card-pred">{formatPredicate(rule.predicate)}</span>
        <span className="rp-card-brace">{"}"}</span>
      </div>
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .rp {
        display: flex; flex-direction: column;
        height: 100%;
        background: var(--bg-1);
        border: 2px solid var(--border);
        border-radius: var(--radius-2);
        overflow: hidden;
      }
      .rp-head {
        display: flex; align-items: baseline; gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-bottom: 2px solid var(--border);
        background: var(--bg-2);
      }
      .rp-title {
        flex: 1;
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 12px;
        letter-spacing: 0.1em;
      }
      .rp-count {
        font-family: var(--font-mono);
        font-size: 11px;
        background: var(--blueprint);
        color: #fff;
        padding: 2px 8px;
        border-radius: 999px;
        font-weight: 700;
      }
      .rp-list {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-3);
        display: flex; flex-direction: column;
        gap: var(--space-3);
      }
      .rp-card {
        background: #fff;
        border: 2px solid var(--border);
        border-radius: var(--radius-1);
        padding: var(--space-3);
        font-family: var(--font-mono);
        font-size: 12px;
        transition: background 0.6s ease, border-color 0.6s ease;
      }
      .rp-card-new {
        background: var(--caution-yellow-soft);
        border-color: var(--caution-yellow);
        animation: rp-pop 1.8s ease;
      }
      @keyframes rp-pop {
        0%   { transform: scale(1); }
        15%  { transform: scale(1.04); }
        100% { transform: scale(1); }
      }
      .rp-card-head {
        display: flex; align-items: baseline; gap: var(--space-2);
        margin-bottom: 4px;
      }
      .rp-card-kw {
        color: var(--blueprint);
        font-weight: 700;
      }
      .rp-card-name {
        color: var(--fg-0);
        font-weight: 600;
      }
      .rp-card-body {
        display: flex; align-items: baseline; gap: 6px;
        padding-left: var(--space-3);
      }
      .rp-card-brace {
        color: var(--fg-2);
      }
      .rp-card-pred {
        color: var(--blueprint);
      }
    `}</style>
  );
}
